import { action, createAsyncAction } from 'typesafe-actions'
import { Dispatch } from 'redux'
import api from 'api/graphql/graphql'
import { AppState } from 'redux/reducers'
import { SearchFilters } from 'reducers/search-reducer'
import {
  GenderFilter,
  Paging,
  Player,
  PublicPersonFilterOptions,
  PublicPersonRatingFilterOptions,
  RangeFilter,
  RangeNumberOperators,
  Sorting,
} from 'types/global'
import {
  HIGH_CONFIDENCE_THRESHOLD,
  WTN_SCALE_HIGH as WTN_MAX,
  WTN_SCALE_LOW as WTN_MIN,
} from 'constants/global'
import { SexEnum } from 'types/api/enums'
import { toast } from 'react-toastify'
import i18n from 'constants/strings'

export const CLEAR_SUGGESTIONS = 'searchbar/CLEAR_PLAYER_SUGGESTIONS'
export const SUGGESTION_REQUESTED = 'searchbar/SUGGESTION_REQUESTED'
export const SUGGESTION_COMPLETED = 'searchbar/SUGGESTION_COMPLETED'
export const SUGGESTION_FAILED = 'searchbar/SUGGESTION_FAILED'
export const SET_COUNTRIES = 'searchbar/SET_COUNTRIES'
export const SET_FILTERS = 'searchbar/SET_FILTERS'
export const SET_SORTING = 'searchbar/SET_SORTING'
export const SET_SEARCH_QUERY = 'searchbar/SET_SEARCH_QUERY'
export const RESET_FILTERS = 'searchbar/RESET_FILTERS'
export const RESET_SEARCH_RESULTS = 'searchbar/RESET_SEARCH_RESULTS'
export const SEARCH_REQUESTED = 'searchbar/SEARCH_REQUESTED'
export const SEARCH_COMPLETED = 'searchbar/SEARCH_COMPLETED'
export const SEARCH_FAILED = 'searchbar/SEARCH_FAILED'

export const performSearch = createAsyncAction(SEARCH_REQUESTED, SEARCH_COMPLETED, SEARCH_FAILED)<
  { searchId: string; queryId: string },
  { searchId: string; results: any[]; total: number; queryId: string },
  { searchId: string; queryId: string }
>()
export const requestSuggestion = createAsyncAction(
  SUGGESTION_REQUESTED,
  SUGGESTION_COMPLETED,
  SUGGESTION_FAILED
)<
  { searchId: string },
  { searchId: string; suggestions: Player[] },
  { error: Error; searchId: string }
>()

export const resetResults = (searchId: string) => action(RESET_SEARCH_RESULTS, { searchId })
export const resetFilters = (searchId: string) => action(RESET_FILTERS, { searchId })
export const setFilters = (filter, searchId: string) => action(SET_FILTERS, { filter, searchId })
export const setSorting = (sorting: Sorting | null, searchId: string) =>
  action(SET_SORTING, { sorting, searchId })
export const setCountries = countries => action(SET_COUNTRIES, countries)
export const clearSuggestions = (searchId: string) => action(CLEAR_SUGGESTIONS, { searchId })
export const setSearchQuery = (searchQuery: string, searchId: string) => {
  return action(SET_SEARCH_QUERY, { searchQuery, searchId })
}

const arrObjToMap = (arr: any[], key: string, transformAttr?: (obj) => any) =>
  arr.reduce((acc, obj) => {
    const attributes = transformAttr ? transformAttr(obj) : obj
    return { ...acc, [obj[key]]: attributes }
  }, {})

/**
 * Multiple keys for the different stores for the different search bars
 * E.g. do not want to share suggestions between search bars.
 */
// [TODO] - Add support for filters (gender, country, rating)
const SUGGESTION_LIMIT = 5
export const suggestPlayers = (
  searchWord: string,
  searchId: string,
  filterOptions: PublicPersonFilterOptions = {}
) => {
  return (dispatch: Dispatch) => {
    const queryRef = `getPlayersSuggestions.${searchId}`
    dispatch(requestSuggestion.request({ searchId }))
    api.cancelPendingQuery(queryRef)
    api
      .getPlayerSuggestions(
        queryRef,
        { ...filterOptions, search: { term: searchWord, autocomplete: true } },
        { limit: SUGGESTION_LIMIT }
      )
      .then(r => {
        dispatch(requestSuggestion.success({ searchId, suggestions: r }))
      })
      .catch(error => {
        if (error?.name === 'AbortError') return
        console.warn('Error fetching player suggestions: ', error.message)
        dispatch(clearSuggestions(searchId))
        dispatch(requestSuggestion.failure({ error, searchId }))
      })
  }
}

export const getCountries = () => {
  return (dispatch: Dispatch) => {
    try {
      const data = require('../api/all-countries.json')
      const countries =
        data &&
        arrObjToMap(Object.values(data), 'alpha-3', obj => ({
          name: obj.name,
          'alpha-2': obj['alpha-2'],
        }))
      dispatch(setCountries(countries))
    } catch (e) {
      console.log('Could not fetch countries: ', e)
    }
  }
}

// gql gender filters requires an enum of 'M', 'F' or 'U'
const genderToGqlParam = (gender: GenderFilter): SexEnum | undefined => {
  const genderFilterMap = {
    [GenderFilter.MALE]: SexEnum.M,
    [GenderFilter.FEMALE]: SexEnum.F,
  }

  return genderFilterMap[gender]
}

const ratingsToGqlParam = (range: RangeFilter, bound: 'L' | 'U') => {
  if (range) {
    return bound === 'L' ? range.min : range.max
  } else {
    return null
  }
}

interface PlayerSearchOptions {
  searchQuery?: string
  filters?: SearchFilters
}

export const performPlayerSearch = (
  opts: PlayerSearchOptions,
  paging: Paging,
  searchId: string
) => {
  return async (dispatch: Dispatch, getState) => {
    const { searchbar }: AppState = getState()
    // If a search query is passed into the component this will override the query
    // that is stored in redux (this is so url query params can be passed in)
    const searchQuery = searchbar.searchQuery[searchId] || opts.searchQuery
    const sorting = searchbar.sorting[searchId] ?? undefined
    const { gender, rating, countries, ageGroup, ...filters } =
      opts.filters || searchbar.filters[searchId] || {}

    let ratingFilters: PublicPersonRatingFilterOptions[] = []
    if (rating) {
      const { type, confidence, range, gameZone } = rating
      const appliedRange = gameZone ?? range

      ratingFilters.push({
        type: type,
        rating: {
          gte: ratingsToGqlParam(appliedRange, 'L') || WTN_MAX,
          lte: ratingsToGqlParam(appliedRange, 'U') || WTN_MIN,
        },
        confidence: confidence ? { gt: HIGH_CONFIDENCE_THRESHOLD } : undefined,
      })
    }

    const makeAgeGroupFilter = (ageGroup: string | undefined) => {
      if (!ageGroup || typeof ageGroup !== 'string') return {}

      const FIXED_AGE_GROUPS_MAP: { [key: string]: RangeNumberOperators } = {
        UNDER_14: { lt: 14 },
        '20_30': { gte: 20, lte: 30 },
        '31_40': { gt: 30, lte: 40 },
        '41_50': { gt: 40, lte: 50 },
        OVER_50: { gt: 50 },
      }

      const ageFilter = FIXED_AGE_GROUPS_MAP[ageGroup]
      if (ageFilter) return { age: ageFilter }

      const birthYear = parseInt(ageGroup)
      return !isNaN(birthYear) && typeof birthYear === 'number' ? { birthYear } : {}
    }

    const gqlFilters: PublicPersonFilterOptions = {
      sex: gender ? genderToGqlParam(gender) : undefined,
      ratings: ratingFilters.length ? ratingFilters : undefined,
      nationalityCodes: countries?.length ? countries : undefined,
      search: searchQuery ? { term: searchQuery } : undefined,
      ...makeAgeGroupFilter(ageGroup),
      ...(filters || {}),
    }

    const queryId = `${searchId}.${new Date().getTime()}`
    try {
      dispatch(performSearch.request({ searchId, queryId }))
      const { results, total } = await api.playerSearch(gqlFilters, paging, sorting)
      dispatch(
        performSearch.success({
          searchId,
          results,
          total,
          queryId,
        })
      )
    } catch (e) {
      console.log('Could not fetch player results', e && (e as Error)?.message)
      toast.error(i18n.t('strAlerts.errorPlayerSearch'))
      dispatch(performSearch.failure({ searchId, queryId }))
    }
  }
}
